/**
 * Pantalla inicial — onboarding a pantalla completa con Galia
 */

import { useEffect, useState } from 'react';
import { UserConfiguration, PracticeMode, AccentType, PersonalityType, MCERLevel, FunctionalityType } from '../../types';
import { StarRating } from './StarRating';
import { LevelProgress } from './LevelProgress';
import { StreakAchievementModal } from './StreakAchievementModal';
import gamificationService from '../../services/gamificationService';
import configService from '../../services/configService';
import authService from '../../services/authService';
import { isKioskMode, isWorkspaceLayoutEnabled } from '../../utils/featureFlags';
import { useWorkspaceFonts } from '../Layout/WorkspaceLayout';
import { LevelProgress as LevelProgressType } from '../../types';
import './InitialScreen.css';
import '../Layout/WorkspaceTheme.css';

/* ── SVG Icons (inline, no deps) ── */

const IconHeart = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

const IconSparkle = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.09 6.26L20 10.27l-4.91 3.82L16.18 22 12 18.27 7.82 22l1.09-7.91L4 10.27l5.91-2.01L12 2z"/>
  </svg>
);

const IconGradCap = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10l-10-5L2 10l10 5 10-5z"/>
    <path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/>
    <path d="M22 10v6"/>
  </svg>
);

const IconChat = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
  </svg>
);

const IconFlame = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
  </svg>
);

const IconClock = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12 6 12 12 16 14"/>
  </svg>
);

const IconMessages = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const IconVolumeOn = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
  </svg>
);

const IconVolumeOff = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
    <line x1="23" y1="9" x2="17" y2="15"/>
    <line x1="17" y1="9" x2="23" y2="15"/>
  </svg>
);

interface InitialScreenProps {
  initialEmail?: string | null;
  onConfigurationComplete: (config: UserConfiguration) => void;
  /**
   * 'fullscreen' (default): pantalla inicial completa con hero, stats y CTA "¡A practicar!".
   * 'sidebar': solo la columna de configuracion, con header y CTA "Aplicar". Para cambiar la
   * configuracion sin volver al menu inicial (ver App.tsx, showSettingsPanel).
   */
  variant?: 'fullscreen' | 'sidebar';
  /** Callback para cerrar el panel cuando variant='sidebar'. */
  onClose?: () => void;
  /**
   * Modo activo de la sesión (solo relevante en variant='sidebar'). En modo
   * profesor (actividades-docente) la configuración se simplifica: no se ajusta
   * nivel ni personalidad.
   */
  currentMode?: PracticeMode;
  /** Estado del audio (TTS) de Galia, para el toggle del sidebar. */
  audioEnabled?: boolean;
  /** Alternar el audio (TTS) de Galia. */
  onToggleAudio?: () => void;
}

const DISABLED_MODES: PracticeMode[] = ['examen'];

// Sprint Galia 1.5: acento europeo unico — todos los acentos seleccionables
// resuelven a fr-FR-Chirp3-HD-Achernar (ver src/config/voiceConfig.ts).
const EU_VOICE = { voice: 'fr-FR-Chirp3-HD-Achernar', language: 'fr-FR' };
const voiceDictionary: Record<string, { voice: string; language: string }> = {
  'Woman-Canadá': EU_VOICE,
  'Woman-Francia': EU_VOICE,
  'Woman-Francia2': EU_VOICE,
  'Woman-Bélgica': EU_VOICE,
};

export const getPromptReference = (mode: PracticeMode, isTestingPhase: boolean): string => {
  const suffix = isTestingPhase ? '-dev' : '-pro';
  const baseMap: Record<PracticeMode, string> = {
    'conversacional': 'alianzafrancesa-galia-conversacional',
    'estudio': 'alianzafrancesa-galia-ruta-academica',
    'examen': 'alianzafrancesa-galia-examen',
    'actividades-docente': 'alianzafrancesa-galia-actividades-docente',
    'actividades-estudiante': 'alianzafrancesa-galia-actividades-estudiante',
  };
  return `${baseMap[mode]}${suffix}`;
};

const PERSONALITY_META: Record<PersonalityType, { img: string; desc: string }> = {
  'Neutral': { img: '/assets/ui/otros/amigable.png', desc: 'Equilibrada y clara' },
  'Amigable': { img: '/assets/ui/otros/amigable.png', desc: 'Motivadora y paciente' },
  'Gruñona': { img: '/assets/ui/otros/sarcastica.png', desc: 'Exigente y directa' },
  'Sarcástica': { img: '/assets/ui/otros/sarcastica.png', desc: 'Humor francés afilado' },
  'Graciosa': { img: '/assets/ui/otros/amigable.png', desc: 'Divertida y creativa' },
};

const MCER_META: Record<MCERLevel, string> = {
  'A1': 'Principiante',
  'A2': 'Elemental',
  'B1': 'Intermedio',
  'B2': 'Intermedio alto',
  'C1': 'Avanzado',
  'C2': 'Maestría',
};

const FUNCTIONALITY_META: Record<FunctionalityType, { img: string; desc: string }> = {
  'Profe': { img: '/assets/ui/otros/profesor.png', desc: 'Clases estructuradas' },
  'Conversadora': { img: '/assets/ui/otros/conversadora.png', desc: 'Conversación libre' },
};

/* SVG flags — Windows doesn't render emoji flags */
const FlagFrance = () => (
  <svg width="28" height="20" viewBox="0 0 30 20" style={{ borderRadius: 3, flexShrink: 0 }}>
    <rect width="10" height="20" fill="#002395"/>
    <rect x="10" width="10" height="20" fill="#fff"/>
    <rect x="20" width="10" height="20" fill="#ED2939"/>
  </svg>
);

const FlagCanada = () => (
  <svg width="28" height="20" viewBox="0 0 30 20" style={{ borderRadius: 3, flexShrink: 0 }}>
    <rect width="30" height="20" fill="#fff"/>
    <rect width="7.5" height="20" fill="#FF0000"/>
    <rect x="22.5" width="7.5" height="20" fill="#FF0000"/>
    <path d="M15 4l-1.5 3-2-0.5 1.5 2.5-2 1 2.5 0.5L13 13h4l-0.5-2.5 2.5-0.5-2-1 1.5-2.5-2 0.5z" fill="#FF0000"/>
  </svg>
);

const ACCENT_META: Record<AccentType, { Flag: React.FC }> = {
  'Francia': { Flag: FlagFrance },
  'Canadá': { Flag: FlagCanada },
  'Australia': { Flag: FlagFrance }, // placeholder
  'Bélgica': { Flag: FlagFrance },  // placeholder
};

export function InitialScreen({ initialEmail, onConfigurationComplete, variant = 'fullscreen', onClose, currentMode, audioEnabled, onToggleAudio }: InitialScreenProps) {
  const isSidebar = variant === 'sidebar';
  // En el sidebar del modo profesor la configuración se simplifica: sin nivel,
  // sin personalidad, sin funcionalidad ni acento (el profesor maneja todo eso).
  const isProfessorSidebar = isSidebar && currentMode === 'actividades-docente';
  // Rediseño 1a (solo dev): la pantalla inicial usa la paleta nueva. El sidebar
  // ya la recibe por html.ws-theme; la pantalla completa se pinta antes de que
  // exista el layout, así que lleva su propia marca (.is-ws).
  const wsTheme = isWorkspaceLayoutEnabled();
  useWorkspaceFonts(wsTheme);
  const [loading, setLoading] = useState(true);
  const [selectedMode, setSelectedMode] = useState<PracticeMode | null>('conversacional');
  const [selectedRasgo, setSelectedRasgo] = useState<PersonalityType | ''>('Amigable');
  const [selectedAcento, setSelectedAcento] = useState<AccentType | ''>('Francia');
  const [selectedMCER, setSelectedMCER] = useState<MCERLevel>('B1');
  const [selectedFunctionality, setSelectedFunctionality] = useState<FunctionalityType>('Conversadora');

  const [userTitle, setUserTitle] = useState<string | null>(null);
  const [streak, setStreak] = useState(0);
  const [streak5Count, setStreak5Count] = useState(0);
  const [levelProgress, setLevelProgress] = useState<LevelProgressType | null>(null);
  const [totalTime, setTotalTime] = useState(0);
  const [totalInteractions, setTotalInteractions] = useState(0);

  const [unlockedAccents, setUnlockedAccents] = useState<AccentType[]>(['Francia']);

  // ── Modo kiosco (pantalla de recepción AF, ?kiosk=1, solo dev) ──
  // Portada de bienvenida con UN botón: cada visitante empieza su propia
  // interacción (pedido de Santiago 26/08) — antes arrancaba solo y el
  // siguiente visitante caía en la conversación del anterior. La config es
  // fija (A1/Conversadora/Amigable): el visitante no elige nada.
  const kioskMode = isKioskMode() && !isSidebar;
  const [kioskStarting, setKioskStarting] = useState(false);
  useEffect(() => {
    if (loading || !kioskMode) return;
    setSelectedMode('conversacional');
    setSelectedRasgo('Amigable');
    setSelectedAcento('Francia');
    setSelectedMCER('A1');
    setSelectedFunctionality('Conversadora');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, kioskMode]);
  // El clic del visitante es el gesto que desbloquea el audio en iOS/Safari,
  // así que handleConfirm debe correr DENTRO de ese gesto (sin await previo).
  const handleKioskStart = () => {
    if (kioskStarting) return;
    setKioskStarting(true);
    handleConfirm();
  };
  const [unlockedPersonalities, setUnlockedPersonalities] = useState<PersonalityType[]>(['Neutral']);
  const [showStreak5Modal, setShowStreak5Modal] = useState(false);

  const config = configService.getConfig();
  const email = (initialEmail ?? config.email ?? '').trim();
  const isTestingPhase = config.testingPhase ?? true;
  // El rol se decide por el dominio del correo institucional de la AF (fuente de
  // verdad, igual que el backend). Solo el personal de la AF
  // (@alianzafrancesa.edu.co) ve el modo profesor; los estudiantes (correos
  // personales) NO pueden entrar como profesor. El rol LTI ya no se usa para esto
  // porque la resolución async de Apolearn lo degradaba a "student". En dev se
  // puede forzar con ?force_role=teacher|student para QA.
  const isDocente = authService.getEffectiveRole(email, isTestingPhase) === 'teacher';

  useEffect(() => {
    configService.loadPersonalizationFromCache();
    const config = configService.getConfig();
    if (config.personalityType) setSelectedRasgo(config.personalityType as PersonalityType);
    if (config.functionalityType) setSelectedFunctionality(config.functionalityType as FunctionalityType);
    if (config.mcerLevel) setSelectedMCER(config.mcerLevel as MCERLevel);
  }, []);

  useEffect(() => {
    const loadApolearnData = async () => {
      const existingData = authService.getApolearnUserData();
      if (existingData && existingData.role) return;
      const config = configService.getConfig();
      const emailToUse = config.email;
      const userIdToUse = config.userID;
      if (!emailToUse && !userIdToUse) return;
      try {
        await authService.getApolearnCompleteData(emailToUse || '', userIdToUse);
      } catch { /* silent */ }
    };
    loadApolearnData();

    const loadUserData = async () => {
      setLoading(true);
      if (!email) {
        setUnlockedAccents(['Francia']);
        setUnlockedPersonalities(['Neutral']);
        setLoading(false);
        return;
      }
      try {
        const currentStreak = await gamificationService.registerDailyConnection(email);
        setStreak(currentStreak);
        const streak5Achievement = await gamificationService.checkStreak5Achievement(email, currentStreak);
        if (streak5Achievement) {
          setShowStreak5Modal(true);
          await gamificationService.checkStreak5Milestone(email, currentStreak);
        }
        const [title, streak5, levelProg, time, interactions, accents, personalities] = await Promise.all([
          gamificationService.getCurrentTitle(email),
          gamificationService.getStreak5Count(email),
          gamificationService.getLevelProgress(email),
          gamificationService.getTotalPracticeTime(email),
          gamificationService.getTotalInteractions(email),
          gamificationService.getUnlockedAccents(email),
          gamificationService.getUnlockedPersonalities(email),
        ]);
        setUserTitle(title);
        setStreak5Count(streak5);
        setLevelProgress(levelProg);
        setTotalTime(time);
        setTotalInteractions(interactions);
        setUnlockedAccents(Array.from(new Set(['Francia', ...accents])) as AccentType[]);
        setUnlockedPersonalities(Array.from(new Set(['Neutral', ...personalities])) as PersonalityType[]);
      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
      } finally {
        setLoading(false);
      }
    };
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const handleConfirm = () => {
    const mode = selectedMode ?? 'conversacional';
    const rasgo = selectedRasgo || 'Amigable';
    const acento = selectedAcento || 'Francia';
    const key = `Woman-${acento}`;
    const voiceInfo = voiceDictionary[key] || voiceDictionary['Woman-Francia'];
    configService.setPersonalityType(rasgo);
    configService.setFunctionalityType(selectedFunctionality);
    configService.setMCERLevel(selectedMCER);
    const userConfig: UserConfiguration = {
      mode,
      promptReference: getPromptReference(mode, isTestingPhase),
      rasgo,
      personalityType: rasgo as PersonalityType,
      functionalityType: selectedFunctionality,
      mcerLevel: selectedMCER,
      acento,
      voice: voiceInfo.voice,
      language: voiceInfo.language,
    };
    onConfigurationComplete(userConfig);
  };

  // Modo profesor (aide-prof): lanza la sesión de asistente pedagógico.
  // Reutiliza el nivel MCER como contexto; el resto de opciones de estudiante
  // (personalidad/acento) no aplican aquí pero se pasan por compatibilidad.
  const handleEnterAideProf = () => {
    const acento = selectedAcento || 'Francia';
    const key = `Woman-${acento}`;
    const voiceInfo = voiceDictionary[key] || voiceDictionary['Woman-Francia'];
    configService.setMCERLevel(selectedMCER);
    const userConfig: UserConfiguration = {
      mode: 'actividades-docente',
      promptReference: getPromptReference('actividades-docente', isTestingPhase),
      rasgo: selectedRasgo || 'Amigable',
      personalityType: (selectedRasgo || 'Amigable') as PersonalityType,
      functionalityType: selectedFunctionality,
      mcerLevel: selectedMCER,
      acento,
      voice: voiceInfo.voice,
      language: voiceInfo.language,
    };
    onConfigurationComplete(userConfig);
  };

  if (loading) {
    return (
      <div className={isSidebar ? 'is-sidebar' : `is-fullscreen ${wsTheme ? 'is-ws' : ''}`}>
        <div className="is-loading">
          <div className="is-loading-bird">
            <img src="/assets/ui/avatares/Galia_.png" alt="Galia" className="is-loading-img" />
          </div>
          <p className="is-loading-text">Cargando...</p>
        </div>
      </div>
    );
  }

  // ── Portada del kiosco: una sola llamada a la acción ──
  // Sin selectores ni estadísticas: el visitante de recepción solo pulsa
  // "Comenzar" y habla. El clic arranca SU sesión (y desbloquea el audio).
  if (kioskMode) {
    return (
      <div className="is-fullscreen is-kiosk">
        <div className="is-kiosk-card">
          <img src="/assets/ui/avatares/Galia_.png" alt="Galia" className="is-kiosk-bird" />
          <h1 className="is-kiosk-title">Bonjour ! Je suis Galia</h1>
          <p className="is-kiosk-sub">
            Tu asistente de francés de la Alliance Française.
            <br />
            ¿Practicamos un momento?
          </p>
          <button
            type="button"
            className="is-kiosk-cta"
            onClick={handleKioskStart}
            disabled={kioskStarting}
          >
            {kioskStarting ? 'Un instant…' : 'Comenzar interacción'}
          </button>
          <p className="is-kiosk-hint">
            Habla con Galia en francés y gana tus primeros trofeos 🏆
          </p>
        </div>
      </div>
    );
  }

  // Sección de audio (TTS) — solo en el sidebar, donde ya hay una sesión activa.
  const audioSection = isSidebar && onToggleAudio ? (
    <section className="is-section" style={{ '--delay': '0.45s' } as React.CSSProperties}>
      <h3 className="is-label">{isProfessorSidebar ? 'Voix de Galia' : 'Audio de Galia'}</h3>
      <button
        type="button"
        className={`is-card ${audioEnabled ? 'is-card--on' : ''}`}
        onClick={onToggleAudio}
      >
        <span className="is-card-icon">{audioEnabled ? <IconVolumeOn /> : <IconVolumeOff />}</span>
        <span className="is-card-body">
          <strong>
            {isProfessorSidebar
              ? (audioEnabled ? 'Voix activée' : 'Voix coupée')
              : (audioEnabled ? 'Voz activada' : 'Voz silenciada')}
          </strong>
          <span className="is-card-desc">
            {isProfessorSidebar
              ? (audioEnabled ? 'Galia répond avec la voix et le texte' : 'Galia répond uniquement par texte')
              : (audioEnabled ? 'Galia responde con voz y texto' : 'Galia responde solo con texto')}
          </span>
        </span>
        {audioEnabled && <span className="is-card-check"><IconCheck /></span>}
      </button>
    </section>
  ) : null;

  return (
    <div className={isSidebar ? 'is-sidebar' : `is-fullscreen ${wsTheme ? 'is-ws' : ''}`}>

      {/* Header del sidebar (solo en variante sidebar) */}
      {isSidebar && (
        <div className="is-sidebar-header">
          <h2 className="is-sidebar-title">{isProfessorSidebar ? 'Configuration' : 'Configuración'}</h2>
          <button
            type="button"
            className="is-sidebar-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Left / Top: Galia + identity (solo fullscreen) ── */}
      {!isSidebar && (
      <div className="is-hero">
        <div className="is-brand">
          <img src="/assets/ui/otros/galia.png" alt="Galia" className="is-logo" draggable={false} />
          <span className="is-brand-sep" />
          <img src="/assets/ui/otros/logo_af.png" alt="Alianza Francesa" className="is-logo-af" draggable={false} />
        </div>
        <div className="is-galia-frame">
          <img
            src="/assets/ui/avatares/Galia_.png"
            alt="Galia"
            className="is-galia-img"
            draggable={false}
          />
        </div>

        <div className="is-speech">
          <p><strong>Salut !</strong> Configura mi estilo para que nuestra práctica de francés sea perfecta.</p>
        </div>

        {userTitle && <div className="is-badge">{userTitle}</div>}

        <div className="is-stats">
          <div className="is-stat">
            <span className="is-stat-icon"><IconFlame /></span>
            <span className="is-stat-val">{streak}</span>
            <span className="is-stat-lbl">Racha</span>
          </div>
          <div className="is-stat">
            <span className="is-stat-icon"><IconClock /></span>
            <span className="is-stat-val">{formatTime(totalTime)}</span>
            <span className="is-stat-lbl">Tiempo</span>
          </div>
          <div className="is-stat">
            <span className="is-stat-icon"><IconMessages /></span>
            <span className="is-stat-val">{totalInteractions}</span>
            <span className="is-stat-lbl">Charlas</span>
          </div>
        </div>

      </div>
      )}

      {/* ── Right / Bottom: Config ── */}
      <div className="is-config">

        {isProfessorSidebar ? (
          <>
            {/* Modo profesor: configuración simplificada (sin nivel ni personalidad) */}
            <section
              className="is-section"
              style={{
                '--delay': '0.05s',
                border: '1.5px solid rgba(237, 109, 51, 0.55)',
                borderRadius: 16,
                padding: 14,
                background: 'rgba(237, 109, 51, 0.06)',
              } as React.CSSProperties}
            >
              <h3 className="is-label">Mode assistante</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="is-card-icon" style={{ flexShrink: 0 }}><IconGradCap /></span>
                <span className="is-card-body">
                  <strong>Galia — Assistante du professeur</strong>
                  <span className="is-card-desc">
                    Je t'aide à préparer tes cours, tes activités et tes contenus. Pas besoin de
                    configurer le niveau de langue ni la personnalité : je m'occupe de tout.
                  </span>
                </span>
              </div>
            </section>

            {/* Audio de Galia (silenciado por defecto en modo profesor) */}
            {audioSection}

            {/*
              Cambio de modo (pedido de Santiago, reunión 15/09/2026): desde el
              modo asistente el botón de configuración solo permitía encender o
              apagar la voz, así que para practicar francés había que cerrar la
              sesión y volver a entrar. El sentido contrario (estudiante →
              asistente) ya existía más abajo, en la rama de estudiante.
              Se reutiliza la última configuración de estudiante en caché; al
              entrar, este mismo panel ya deja ajustar nivel, personalidad y
              funcionalidad.
            */}
            <section className="is-section" style={{ '--delay': '0.55s' } as React.CSSProperties}>
              <h3 className="is-label">Changer de mode</h3>
              <button type="button" className="is-card" onClick={handleConfirm}>
                <span className="is-card-icon"><IconChat /></span>
                <span className="is-card-body">
                  <strong>Passer en conversation</strong>
                  <span className="is-card-desc">
                    Pratique le français avec Galia — {selectedMCER} · {selectedFunctionality} ·{' '}
                    {selectedRasgo || 'Amigable'}. Tu peux tout régler ensuite ici même.
                  </span>
                </span>
              </button>
            </section>

            <button className="is-cta" onClick={onClose}>
              <span>Terminé</span>
              <IconArrowRight />
            </button>
          </>
        ) : (
          <>

        {/* Modo profesor (aide-prof) — entrada separada, solo para profesores */}
        {isDocente && (
          <section
            className="is-section"
            style={{
              '--delay': '0.05s',
              border: '1.5px solid rgba(237, 109, 51, 0.55)',
              borderRadius: 16,
              padding: 14,
              background: 'rgba(237, 109, 51, 0.06)',
            } as React.CSSProperties}
          >
            <h3 className="is-label">Mode assistante</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <span className="is-card-icon" style={{ flexShrink: 0 }}><IconGradCap /></span>
              <span className="is-card-body">
                <strong>Galia — Assistante du professeur</strong>
                <span className="is-card-desc">
                  Conçois des activités et des exercices avec Galia pour tes élèves, en classe et à la maison.
                </span>
              </span>
            </div>
            <button type="button" className="is-cta" onClick={handleEnterAideProf}>
              <span>Entrer en mode assistante</span>
              <IconArrowRight />
            </button>
          </section>
        )}

        {/* Personalidad */}
        <section className="is-section" style={{ '--delay': '0.1s' } as React.CSSProperties}>
          <h3 className="is-label">Personalidad</h3>
          <div className="is-row">
            {(['Amigable', 'Sarcástica'] as PersonalityType[]).map((p) => {
              const { img, desc } = PERSONALITY_META[p];
              const active = selectedRasgo === p;
              return (
                <button key={p} type="button" className={`is-card ${active ? 'is-card--on' : ''}`} onClick={() => setSelectedRasgo(p)}>
                  <span className="is-card-icon"><img src={img} alt={p} className="is-card-img" /></span>
                  <span className="is-card-body">
                    <strong>{p}</strong>
                    <span className="is-card-desc">{desc}</span>
                  </span>
                  {active && <span className="is-card-check"><IconCheck /></span>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Nivel MCER */}
        <section className="is-section" style={{ '--delay': '0.2s' } as React.CSSProperties}>
          <h3 className="is-label">Nivel MCER</h3>
          <div className="is-grid-mcer">
            {(['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as MCERLevel[]).map((lvl) => (
              <button key={lvl} type="button" className={`is-pill ${selectedMCER === lvl ? 'is-pill--on' : ''}`} onClick={() => setSelectedMCER(lvl)}>
                <strong>{lvl}</strong>
                <span className="is-pill-sub">{MCER_META[lvl]}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Funcionalidad */}
        <section className="is-section" style={{ '--delay': '0.3s' } as React.CSSProperties}>
          <h3 className="is-label">Funcionalidad</h3>
          <div className="is-row">
            {(['Profe', 'Conversadora'] as FunctionalityType[]).map((f) => {
              const { img, desc } = FUNCTIONALITY_META[f];
              const active = selectedFunctionality === f;
              return (
                <button key={f} type="button" className={`is-card ${active ? 'is-card--on' : ''}`} onClick={() => setSelectedFunctionality(f)}>
                  <span className="is-card-icon"><img src={img} alt={f} className="is-card-img" /></span>
                  <span className="is-card-body">
                    <strong>{f}</strong>
                    <span className="is-card-desc">{desc}</span>
                  </span>
                  {active && <span className="is-card-check"><IconCheck /></span>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Acento: SIN sección. Hoy hay un solo acento (todas las voces
            resuelven a la europea, ver voiceDictionary) y Santiago pidió
            quitarlo en la reunión del 15/09/2026 («¿para qué una opción si es
            la única?»; la transcripción lo tradujo mal y no llegó al resumen —
            lo repitió el 21/09). El valor interno sigue siendo «Francia»
            (selectedAcento). Si vuelven más acentos, la sección regresa. */}

        {/* Audio de Galia (solo en el sidebar de estudiante) */}
        {audioSection}

        {/* CTA */}
        <button className="is-cta" onClick={handleConfirm}>
          <span>{isSidebar ? 'Aplicar cambios' : '¡A practicar!'}</span>
          <IconArrowRight />
        </button>
          </>
        )}
      </div>

      {showStreak5Modal && (
        <StreakAchievementModal
          streak5Count={streak5Count}
          onClose={() => setShowStreak5Modal(false)}
        />
      )}
    </div>
  );
}
